// src/components/Public/CategoryListings.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { db } from '../../firebase';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { FaBed, FaBath, FaCar, FaMapMarkerAlt, FaRulerCombined, FaImages, FaHome } from 'react-icons/fa';

function CategoryListings() {
  const { category } = useParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCategoryProperties = async () => {
      try {
        setLoading(true);
        
        // For now, filter by property_type until migration is complete
        const categoryMap = {
          'condo': 'condo',
          'apartment': 'apartment', 
          'landed-house': 'landed house',
          'terrace': 'terrace',
          'semi-d': 'semi-detached'
        };

        const searchTerm = categoryMap[category] || category.replace('-', ' ');
        
        const q = query(
          collection(db, 'properties'),
          where('status', '==', 'Available'),
          orderBy('date_status', 'desc')
        );

        const querySnapshot = await getDocs(q);
        let fetchedProperties = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Filter by property type (case insensitive)
        fetchedProperties = fetchedProperties.filter(property => 
          property.property_type && 
          property.property_type.toLowerCase().includes(searchTerm.toLowerCase())
        );

        setProperties(fetchedProperties);
      } catch (err) {
        console.error("Error fetching category properties:", err);
        setError("Failed to load properties. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryProperties();
  }, [category]);

  const formatCategoryTitle = (category) => {
    const titles = {
      'condo': 'Condominiums',
      'apartment': 'Apartments',
      'landed-house': 'Landed Houses',
      'terrace': 'Terrace Houses',
      'semi-d': 'Semi-Detached Houses'
    };
    return titles[category] || category.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const categoryTitle = formatCategoryTitle(category);

  if (loading) return <div style={loadingStyle}>Loading {categoryTitle.toLowerCase()}...</div>;
  if (error) return <div style={errorStyle}>{error}</div>;

  return (
    <>
      <Helmet>
        <title>{categoryTitle} Properties | Property Listings</title>
        <meta name="description" content={`Browse ${properties.length} available ${categoryTitle.toLowerCase()} in Malaysia. Find your perfect property with detailed listings and photos.`} />
        <link rel="canonical" href={`${window.location.origin}/category/${category}`} />
      </Helmet>

      <div style={containerStyle}>
        {/* Breadcrumb */}
        <nav style={breadcrumbStyle}>
          <Link to="/" style={breadcrumbLinkStyle}>
            <FaHome style={{ marginRight: '5px' }} />
            Home
          </Link>
          <span style={breadcrumbSeparatorStyle}> {'>'} </span>
          <Link to="/" style={breadcrumbLinkStyle}>Properties</Link>
          <span style={breadcrumbSeparatorStyle}> {'>'} </span>
          <span style={breadcrumbCurrentStyle}>{categoryTitle}</span>
        </nav>

        <div style={headerStyle}>
          <h1 style={titleStyle}>{categoryTitle}</h1>
          <p style={subtitleStyle}>
            {properties.length} {properties.length === 1 ? 'property' : 'properties'} available
          </p>
        </div>

        {properties.length === 0 ? (
          <div style={noPropertiesStyle}>
            <p>No {categoryTitle.toLowerCase()} found.</p>
            <Link to="/" style={backLinkStyle}>Browse All Properties</Link>
          </div>
        ) : (
          <div style={gridStyle}>
            {properties.map(property => (
              <PropertyCard key={property.id} property={property} />
            ))}
          </div>
        )}

        {/* Related Categories */}
        <div style={relatedCategoriesStyle}>
          <h3 style={relatedTitleStyle}>Browse Other Property Types</h3>
          <div style={categoryLinksStyle}>
            <Link to="/category/condo" style={categoryLinkStyle}>Condos</Link>
            <Link to="/category/apartment" style={categoryLinkStyle}>Apartments</Link>
            <Link to="/category/landed-house" style={categoryLinkStyle}>Landed Houses</Link>
            <Link to="/category/terrace" style={categoryLinkStyle}>Terrace Houses</Link>
          </div>
        </div>
      </div>
    </>
  );
}

// Simple Property Card Component (inline for now)
const PropertyCard = ({ property }) => (
  <Link to={`/listing/${property.slug || property.id}`} style={cardLinkStyle}>
    <div style={cardStyle}>
      <div style={imageWrapperStyle}>
        <img
          src={property.images && property.images[0] ? property.images[0] : '/placeholder.jpg'}
          alt={property.title}
          style={cardImageStyle}
          loading="lazy"
        />
        {property.images && property.images.length > 1 && (
          <div style={imageCountBadgeStyle}>
            <FaImages style={{ marginRight: '4px' }} />
            {property.images.length}
          </div>
        )}
      </div>
      
      <div style={cardContentStyle}>
        <h3 style={cardTitleStyle}>{property.title}</h3>
        
        {property.display_id && (
          <p style={propertyIdStyle}>ID: {property.display_id}</p>
        )}
        
        <p style={locationStyle}>
          <FaMapMarkerAlt style={iconStyle} />
          {property.location}
        </p>
        
        <div style={featuresRowStyle}>
          {property.bedrooms && (
            <span style={featureItemStyle}>
              <FaBed style={iconStyle} />{property.bedrooms}
            </span>
          )}
          {property.bathrooms && (
            <span style={featureItemStyle}>
              <FaBath style={iconStyle} />{property.bathrooms}
            </span>
          )}
          {property.carparks && (
            <span style={featureItemStyle}>
              <FaCar style={iconStyle} />{property.carparks}
            </span>
          )}
        </div>

        {property.built_up_area && (
          <p style={areaStyle}>
            <FaRulerCombined style={iconStyle} />
            {property.built_up_area.toLocaleString()} sqft
          </p>
        )}

        <div style={priceContainerStyle}>
          {property.listing_type && property.listing_type.includes('sale') && property.price_sale && (
            <div style={priceRowStyle}>
              <span style={priceLabelStyle}>For Sale</span>
              <span style={priceValueStyle}>
                RM {property.price_sale.toLocaleString()}
              </span>
            </div>
          )}
          {property.listing_type && property.listing_type.includes('rent') && property.price_rent && (
            <div style={priceRowStyle}>
              <span style={priceLabelStyle}>For Rent</span>
              <span style={priceValueStyle}>
                RM {property.price_rent.toLocaleString()}/month
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  </Link>
);

// Styles
const containerStyle = {
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '20px',
};

const breadcrumbStyle = {
  padding: '10px 0',
  marginBottom: '20px',
  fontSize: '0.9em',
  color: '#666',
};

const breadcrumbLinkStyle = {
  color: '#007bff',
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
};

const breadcrumbSeparatorStyle = {
  margin: '0 8px',
  color: '#999',
};

const breadcrumbCurrentStyle = {
  color: '#333',
  fontWeight: 'bold',
};

const headerStyle = {
  textAlign: 'center',
  marginBottom: '40px',
  paddingBottom: '20px',
  borderBottom: '2px solid #e9ecef',
};

const titleStyle = {
  fontSize: '2.5em',
  color: '#333',
  margin: '0 0 10px 0',
  fontWeight: 'bold',
};

const subtitleStyle = {
  fontSize: '1.2em',
  color: '#666',
  margin: '0',
};

const loadingStyle = {
  textAlign: 'center',
  fontSize: '1.5em',
  marginTop: '50px',
  color: '#007bff',
};

const errorStyle = {
  textAlign: 'center',
  color: 'red',
  fontSize: '1.2em',
  marginTop: '50px',
};

const noPropertiesStyle = {
  textAlign: 'center',
  padding: '60px 20px',
  backgroundColor: '#f8f9fa',
  borderRadius: '10px',
  margin: '40px 0',
};

const backLinkStyle = {
  display: 'inline-block',
  marginTop: '20px',
  padding: '10px 20px',
  backgroundColor: '#007bff',
  color: 'white',
  textDecoration: 'none',
  borderRadius: '5px',
  fontWeight: 'bold',
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
  gap: '25px',
  marginBottom: '50px',
};

const cardLinkStyle = {
  textDecoration: 'none',
  color: 'inherit',
  display: 'block',
};

const cardStyle = {
  backgroundColor: '#fff',
  borderRadius: '10px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
};

const imageWrapperStyle = {
  position: 'relative',
  width: '100%',
  height: '200px',
  overflow: 'hidden',
  flexShrink: 0,
};

const cardImageStyle = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

const imageCountBadgeStyle = {
  position: 'absolute',
  top: '10px',
  right: '10px',
  backgroundColor: 'rgba(0,0,0,0.7)',
  color: 'white',
  padding: '4px 8px',
  borderRadius: '12px',
  fontSize: '0.8em',
  display: 'flex',
  alignItems: 'center',
};

const cardContentStyle = {
  padding: '20px',
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
};

const cardTitleStyle = {
  fontSize: '1.2em',
  fontWeight: 'bold',
  margin: '0 0 8px 0',
  color: '#333',
  lineHeight: '1.3',
};

const propertyIdStyle = {
  fontSize: '0.8em',
  color: '#888',
  margin: '0 0 5px 0',
};

const locationStyle = {
  fontSize: '0.9em',
  color: '#666',
  margin: '0 0 10px 0',
  display: 'flex',
  alignItems: 'center',
};

const featuresRowStyle = {
  display: 'flex',
  gap: '12px',
  marginBottom: '10px',
  flexWrap: 'wrap',
};

const featureItemStyle = {
  fontSize: '0.85em',
  color: '#555',
  display: 'flex',
  alignItems: 'center',
  backgroundColor: '#f8f9fa',
  padding: '4px 8px',
  borderRadius: '12px',
};

const areaStyle = {
  fontSize: '0.85em',
  color: '#666',
  margin: '0 0 10px 0',
  display: 'flex',
  alignItems: 'center',
};

const iconStyle = {
  marginRight: '6px',
  color: '#007bff',
  fontSize: '1em',
};

const priceContainerStyle = {
  marginTop: 'auto',
  paddingTop: '12px',
  borderTop: '1px solid #eee',
};

const priceRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '5px',
};

const priceLabelStyle = {
  fontSize: '0.85em',
  color: '#666',
  fontWeight: 'normal',
};

const priceValueStyle = {
  fontSize: '1.1em',
  fontWeight: 'bold',
  color: '#007bff',
};

const relatedCategoriesStyle = {
  marginTop: '50px',
  padding: '30px',
  backgroundColor: '#f8f9fa',
  borderRadius: '10px',
};

const relatedTitleStyle = {
  fontSize: '1.5em',
  color: '#333',
  marginBottom: '20px',
  textAlign: 'center',
};

const categoryLinksStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '15px',
  justifyContent: 'center',
};

const categoryLinkStyle = {
  padding: '10px 20px',
  backgroundColor: '#007bff',
  color: 'white',
  textDecoration: 'none',
  borderRadius: '25px',
  fontSize: '0.9em',
  fontWeight: 'bold',
  transition: 'background-color 0.3s ease',
};

export default CategoryListings;